/**
 * Wrapper typed peste /credentials. Toate blob-urile sunt base64 — string-uri
 * opak din perspectiva serverului, decriptate doar în browser.
 */
import { api } from '@/api/client';

export type CredentialType =
  | 'password'
  | 'ssh_key'
  | 'certificate'
  | 'api_key'
  | 'totp_seed'
  | 'db_connection'
  | 'secure_file'
  | 'generic';

export type MetaValue = string | number | boolean | null;

export interface CredentialSummary {
  id: string;
  type: CredentialType;
  title: string;
  url: string | null;
  username: string | null;
  owner_id?: string | null;
  expires_at: string | null;
  meta: Record<string, MetaValue>;
  current_version: number;
  created_at: string;
  updated_at: string;
  pinned: boolean;
  tag_ids: string[];
  access_count: number;
  last_accessed_at: string | null;
  is_break_glass: boolean;
}

export interface CredentialDetail extends CredentialSummary {
  ciphertext: string;
  nonce: string;
  dek_wrapped: string;
}

export interface CredentialCreate {
  type: CredentialType;
  title: string;
  url?: string;
  username?: string;
  expires_at?: string;
  meta?: Record<string, MetaValue>;
  ciphertext: string;
  nonce: string;
  dek_wrapped: string;
}

export interface CredentialUpdate {
  title: string;
  url?: string;
  username?: string;
  /** Absent = păstrează; `null` = șterge data de expirare. */
  expires_at?: string | null;
  meta?: Record<string, MetaValue>;
  ciphertext: string;
  nonce: string;
  dek_wrapped: string;
  /**
   * Dict {user_id -> base64_dek_wrapped} pentru TOȚI userii cu share activ.
   * Obligatoriu dacă credențialul are >1 share (altfel DEK-ul lor rămâne
   * criptat cu DEK-ul vechi → stale). Clientul face fetch /shares înainte
   * de save, wrap cu public_key al fiecărui user, include aici.
   */
  dek_wraps?: Record<string, string>;
  change_reason?: string;
}

export interface CredentialVersionSummary {
  id: string;
  version: number;
  changed_by: string;
  changed_at: string;
  change_reason: string | null;
  change_source: string | null;
}

export interface CredentialVersionDetail extends CredentialVersionSummary {
  ciphertext: string;
  nonce: string;
  dek_wrapped: string;
}

export interface ShareListItem {
  user_id: string;
  email: string;
  permission: string;
  granted_by: string | null;
  granted_at: string;
}

export interface ShareCreate {
  user_id: string;
  dek_wrapped: string;
  permission: 'read' | 'write';
}

export const credentialsApi = {
  list: (tenantId: string) =>
    api.get<CredentialSummary[]>(`/tenants/${tenantId}/credentials`),

  get: (id: string, opts?: { noTrack?: boolean }) =>
    api.get<CredentialDetail>(
      `/credentials/${id}${opts?.noTrack ? '?no_track=1' : ''}`,
    ),

  create: (tenantId: string, req: CredentialCreate) =>
    api.post<CredentialDetail>(`/tenants/${tenantId}/credentials`, req),

  update: (id: string, req: CredentialUpdate) =>
    api.patch<CredentialDetail>(`/credentials/${id}`, req),

  delete: (id: string) => api.delete<null>(`/credentials/${id}`),

  pin: (id: string) => api.post<null>(`/credentials/${id}/pin`),
  unpin: (id: string) => api.delete<null>(`/credentials/${id}/pin`),

  listBreakGlass: (tenantId: string) =>
    api.get<CredentialSummary[]>(
      `/tenants/${tenantId}/credentials/break-glass`,
    ),
  setBreakGlass: (id: string, isBreakGlass: boolean) =>
    api.patch<CredentialSummary>(`/credentials/${id}/break-glass`, {
      is_break_glass: isBreakGlass,
    }),

  listVersions: (id: string) =>
    api.get<CredentialVersionSummary[]>(`/credentials/${id}/versions`),

  getVersion: (id: string, version: number) =>
    api.get<CredentialVersionDetail>(`/credentials/${id}/versions/${version}`),

  listShares: (id: string) =>
    api.get<ShareListItem[]>(`/credentials/${id}/shares`),

  addShare: (id: string, req: ShareCreate) =>
    api.post<ShareListItem>(`/credentials/${id}/shares`, req),

  /**
   * Rotează DEK-ul + (opțional) revocă. Atomic: archivează versiune veche,
   * înlocuiește ciphertext/nonce, re-wraps pt remaining, șterge revocați.
   * Zero-access post-revoke: userii revocați nu mai pot decripta viitor.
   */
  rotate: (
    id: string,
    req: {
      ciphertext: string;
      nonce: string;
      dek_wraps: Record<string, string>;
      revoke?: string[];
      change_reason?: string;
    },
  ) => api.post<CredentialDetail>(`/credentials/${id}/rotate`, req),
};
